import React from 'react';
import classNames from 'classnames';

import { getListingEcolabels } from '../../config/ecolabels';

import css from './EcolabelList.module.css';

/**
 * EcolabelList
 *
 * Renders the ethy ecolabels that apply to a listing as a neat row of badges.
 * Used both on listing cards (below the price/title so shoppers can see a
 * product's credentials before opening it) and on the listing page itself.
 *
 * Nothing is shown unless the listing actually has ticked ecolabels, so this is
 * safe to drop in anywhere — it simply renders null when there's nothing to say.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.publicData] listing.attributes.publicData — ecolabels are derived from it
 * @param {Array} [props.ecolabels] pre-resolved ecolabels (overrides publicData if given)
 * @param {'small'|'medium'|'large'} [props.size='small'] badge sizing preset
 * @param {number} [props.limit] the number of chips the row may show; extra ecolabels are
 *   collapsed into a "+N" chip that takes the last of those places
 * @param {Array<string>} [props.highlightKeys] ecolabel keys to pick out (the shopper's
 *   priorities): they're drawn with a ring and moved to the front, so on a capped row
 *   they're the ones that stay visible
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @returns {JSX.Element|null}
 */
const EcolabelList = props => {
  const {
    publicData,
    ecolabels,
    size = 'small',
    limit,
    highlightKeys = [],
    className,
    rootClassName,
  } = props;

  const resolved = ecolabels || getListingEcolabels(publicData);

  if (!resolved || resolved.length === 0) {
    return null;
  }

  const isHighlighted = ecolabel => highlightKeys.includes(ecolabel.key);
  const items =
    highlightKeys.length > 0
      ? [...resolved.filter(isHighlighted), ...resolved.filter(e => !isHighlighted(e))]
      : resolved;

  // `limit` caps the whole row, not just the badges: when there are more
  // ecolabels than will fit, the "+N" chip takes the last place rather than
  // being added after a full row and wrapping onto a second line — which would
  // make one card in a grid taller than its neighbours.
  const isCapped = typeof limit === 'number' && items.length > limit;
  const shown = isCapped ? items.slice(0, Math.max(limit - 1, 0)) : items;
  const overflow = items.length - shown.length;

  const classes = classNames(rootClassName || css.root, css[size], className, {
    [css.singleRow]: typeof limit === 'number',
  });

  return (
    <ul className={classes}>
      {shown.map(ecolabel => (
        <li
          key={ecolabel.key}
          className={classNames(css.item, { [css.itemHighlighted]: isHighlighted(ecolabel) })}
        >
          <img
            className={css.badge}
            src={ecolabel.src}
            alt={ecolabel.label}
            title={ecolabel.label}
            loading="lazy"
          />
        </li>
      ))}
      {overflow > 0 ? (
        <li className={css.more} aria-label={`${overflow} more`}>
          +{overflow}
        </li>
      ) : null}
    </ul>
  );
};

export default EcolabelList;
